import { useEffect, useRef, useState } from "react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dialog from "../dialog/Dialog";
import { openInstalledAppDialog } from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { Box, List, ListItem } from "@mui/material";
import { sessionService } from "redux-react-session";
import {
  contactSupportIcon,
  loaderBackgroundLayer,
  loaderSpinner,
  privacyPolicyIcon,
  termsOfUseIcon,
} from "../images";
import { CustomPopper } from "../popper/Popper";
import Text from "../text/Text";
import WalletSymbols from "../WalletSymbols";
import {
  contactSupport,
  copyUrl,
  fullScreen,
  lnurl,
  privacyPolicy,
  termsOfUse,
} from "../messages";
import WithdrawModal from "./withdraw/WithdrawModal";
import {
  commonAnalyticsAPI,
  EVENT_TYPES,
  handleGetEventSession,
  isTMA,
  withdrawRequest,
} from "../constants";
import WithdrawSuccessPage from "./withdraw/WithdrawSuccessPage";
import { handleProceed } from "../sendFlowConstants";
import CommonDrawer from "../drawer/CommonDrawer";
import MaintenancePage from "../MaintenancePage";

function InstalledAppDialog() {
  const dispatch = useDispatch();
  const { isOpenInstalledAppDialog, appDetails, payViaMethods } = useSelector(
    (state) => state.common
  );

  const {
    background_color,
    app_url,
    support_email,
    display_name,
    content_color,
    id,
    loader_url,
    under_maintenance,
  } = { ...appDetails?.data };

  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [session, setSession] = useState({});
  const [overlayActive, setOverlayActive] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  //send payment
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(data?.amount || 0);
  const [currencyFromDecode, setCurrencyFromDecode] = useState(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [currencyExchangeRate, setCurrencyExchangeRate] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [isShowErrorModal, setIsShowErrorModal] = useState(false);

  const iframeRef = useRef(null);

  useEffect(() => {
    handleGetSession();
    handleSetEventSession(); //generate event session for events at the time of opening the app

    return () => {
      handleCloseEvent();
    };
  }, []);

  useEffect(() => {
    if (anchorEl) {
      setOverlayActive(true); // keep it enabled until user taps
    } else {
      setOverlayActive(false);
    }
  }, [anchorEl]);

  useEffect(() => {
    if (data || isShowErrorModal) {
      setIsWithdrawModalOpen(true);
    }
  }, [data, isShowErrorModal]);

  const handleVisibilityChange = async () => {
    if (document.visibilityState === "hidden") {
      sessionStorage.setItem("visibility_hidden", Date.now());
      isTMA() && handleAppClose(); //used when mini app minimized and then after closed
    } else if (document.visibilityState === "visible") {
      let hiddenVisibilityTime = sessionStorage.getItem("visibility_hidden");

      if (hiddenVisibilityTime) {
        await commonAnalyticsAPI({
          data: appDetails?.data,
          type: EVENT_TYPES.RESTORE,
          session: sessionStorage.getItem("mapp_session"),
          closeTime: hiddenVisibilityTime,
        });

        sessionStorage.removeItem("visibility_hidden");
      }
    }
  };

  const handleAppClose = () => {
    try {
      const sessionId = sessionStorage.getItem("mapp_session");
      if (sessionId) {
        //to call the close event when the app is again loads
        localStorage.setItem(
          sessionId,
          JSON.stringify({
            session: sessionId,
            app_id: id,
            app_name: display_name,
            close_time: Date.now(),
          })
        );
      }
    } catch (err) {
      console.warn("Failed to persist close event:", err);
    }
  };

  const validateEventData = (raw) => {
    try {
      const parsed = JSON.parse(raw);

      // check top-level fields
      if (
        typeof parsed.version !== "string" ||
        typeof parsed.account_id !== "string" ||
        typeof parsed.data !== "object"
      ) {
        return null;
      }

      const { amount, currency, target_currency, deposit_address } =
        parsed.data;

      // check required data fields
      if (
        typeof amount !== "number" ||
        typeof currency !== "string" ||
        typeof target_currency !== "string" ||
        typeof deposit_address !== "string"
      ) {
        return null;
      }

      return parsed;
    } catch (err) {
      console.error("Invalid JSON:", err);
      return null;
    }
  };

  const handleMessage = (event) => {
    // Verify the message comes from the trusted iframe
    const iframeOrigin = new URL(app_url).origin;
    if (event.origin !== iframeOrigin) {
      console.warn("Rejected message from untrusted origin:", event.origin);
      return;
    }

    const validated = validateEventData(event.data);

    if (validated) {
      const { deposit_address, target_currency } = validated.data;
      // setCurrency(target_currency);
      handleProceed({
        invoiceUrl: deposit_address,
        setAmount,
        setData,
        setBtnLoader,
        currency: target_currency,
        setCurrencyExchangeRate,
        setCurrencyFromDecode,
        setErrorMsg,
        setIsShowErrorModal,
        payViaMethods,
        isFromMiniApp: true,
      });
    }
  };

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange); // when the mini app is open and user switches the tab or changes the screen
    window.addEventListener("pagehide", handleAppClose); // when tab or window closed
    window.addEventListener("message", handleMessage);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handleAppClose);
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const handleCloseEvent = async () => {
    await commonAnalyticsAPI({
      data: appDetails?.data,
      type: EVENT_TYPES.CLOSE,
      closeTime: Date.now(),
      session: sessionStorage.getItem("mapp_session"),
    });
  };

  const onOverlayActivate = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setOverlayActive(false); // remove overlay
    setAnchorEl(null); // close popper
  };

  const handleGetSession = async () => {
    try {
      const session = await sessionService.loadSession();
      setSession(session);
    } catch (error) {
      console.error("Failed to load session:", error);
    }
  };

  const handleSetEventSession = async () => {
    const response = handleGetEventSession("sn_mapp", 8);
    sessionStorage.setItem("mapp_session", response);

    await commonAnalyticsAPI({
      data: appDetails?.data,
      type: EVENT_TYPES.OPEN,
      session: response,
    });
  };

  const modalFooter = (
    <Box
      className="footer-installed-apps"
      sx={{ backgroundColor: `#${background_color}` }}
    ></Box>
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const copyToClipboard = (e, data, setFn, isCallEvent = false) => {
    e.stopPropagation();
    navigator?.clipboard
      ?.writeText(data)
      .then(() => {
        setFn(true);
        setTimeout(async () => {
          setFn(false);
          if (isCallEvent) {
            await commonAnalyticsAPI({
              data: appDetails?.data,
              type: EVENT_TYPES.SHARED,
            });
          }
        }, 1000);
      })
      .catch((error) => {
        console.error("Failed to copy to clipboard:", error);
      });
  };

  function openIframeFullscreen() {
    const iframe = document.getElementById("my-iframe");
    if (iframe.requestFullscreen) {
      iframe.requestFullscreen();
    } else if (iframe.webkitRequestFullscreen) {
      // Safari
      iframe.webkitRequestFullscreen();
    } else if (iframe.msRequestFullscreen) {
      // IE11
      iframe.msRequestFullscreen();
    }
  }

  const handleRedirect = (url) => {
    window.open(process.env.REACT_APP_PROMO_WEBSITE + url);
  };

  const actions = [
    {
      label: copyUrl,
      icon: <WalletSymbols symbol={copied ? "check" : "content_copy"} />,
      action: (e) => {
        if (!copied) {
          copyToClipboard(e, app_url, setCopied, true);
        }
      },
    },
    {
      label: contactSupport,
      icon: <img src={contactSupportIcon} alt="contact" />,
      action: () => {
        window.open(`mailto:${support_email}`, "_blank");
      },
    },
    {
      label: termsOfUse,
      icon: <img src={termsOfUseIcon} alt="terms" />,
      action: () => handleRedirect("/terms-of-use-for-speed-mini-app"),
    },
    {
      label: privacyPolicy,
      icon: <img src={privacyPolicyIcon} alt="policy" />,
      action: () => handleRedirect("/privacy-policy-for-mini-apps"),
    },
    ...(!isTMA()
      ? [
          {
            label: fullScreen,
            icon: <WalletSymbols symbol="fullscreen" />,
            action: openIframeFullscreen,
          },
        ]
      : []),
  ];

  return (
    <Dialog
      isOpenModal={isOpenInstalledAppDialog}
      setIsOpenModal={() => dispatch(openInstalledAppDialog(false))}
      modalHeader={display_name}
      directionToOpen={"up"}
      handleAction={() => dispatch(openInstalledAppDialog(false))}
      isShowCloseIcon={true}
      showFooter={true}
      footerComponent={modalFooter}
      className="installed-app"
      sx={{
        ".MuiToolbar-root": {
          backgroundColor: `#${background_color}`,
          ".MuiTypography-root, .MuiSvgIcon-root": {
            color: `#${content_color}`,
          },
        },
      }}
      additionalSymbol={
        !isIframeLoading && (
          <MoreVertIcon
            style={{ cursor: "pointer" }}
            onClick={(event) =>
              setAnchorEl(anchorEl ? null : event.currentTarget)
            }
          />
        )
      }
    >
      <CustomPopper
        disablePortal={true}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        position="bottom-end"
        handleClose={handleClose}
        className="installed-apps-action"
      >
        <List>
          {actions.map((action) => {
            if (action.label === contactSupport && !support_email) {
              return null;
            }
            return (
              <ListItem
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={action.action}
                key={action.label}
              >
                <Text className="default-text" size={14} font="regular">
                  {action.label}
                </Text>
                <Box>
                  {action.icon}
                  {action.label === contactSupport && (
                    <span
                      onClick={(e) => {
                        e.preventDefault();
                        copyToClipboard(e, support_email, setEmailCopied);
                      }}
                    >
                      <WalletSymbols
                        symbol={emailCopied ? "check" : "content_copy"}
                      />
                    </span>
                  )}
                </Box>
              </ListItem>
            );
          })}
        </List>
      </CustomPopper>

      {under_maintenance ? (
        <MaintenancePage
          customClass="under-maintenance-wrapper"
          isUnderMaintenance={true}
          isFromMiniApp={true}
          closeDialog={() => dispatch(openInstalledAppDialog(false))}
        />
      ) : (
        <>
          {isIframeLoading && (
            <Box
              className={`loading-iframe ${
                !loader_url ? "custom-skeleton" : ""
              }`}
            >
              <img
                src={loader_url || loaderBackgroundLayer}
                alt="Loading"
                height="64px"
                width="64px"
              />
            </Box>
          )}
          <iframe
            id="my-iframe"
            ref={iframeRef}
            tabIndex={0}
            src={
              app_url +
              `?acct=${session?.account?.account?.id}&lang=en&bal_btc=${session?.satsBalance}&bal_usdt=${session?.usdtBalance}&p_add=${session?.lnAddressSATS}&app_id=${id}`
            }
            height="100%"
            width="100%"
            onLoad={() => setIsIframeLoading(false)}
            style={{
              border: "0px",
              display: isIframeLoading ? "none" : "block",
            }}
            allowFullScreen
            allow="fullscreen"
          />
        </>
      )}

      {/*to close the popper when click on the iframe */}
      {overlayActive && (
        <div
          onTouchStart={onOverlayActivate}
          onMouseDown={onOverlayActivate}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 99, // above iframe
            background: "transparent",
          }}
        />
      )}
      {btnLoader && (
        <CommonDrawer
          anchor="bottom"
          open={btnLoader}
          // onClose={handleDrawerClose}
          className="withdraw-drawer"
        >
          <Box
            minHeight="250px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <img src={loaderSpinner} height="25px" width="25px" />
          </Box>
        </CommonDrawer>
      )}

      {isWithdrawModalOpen && (
        <WithdrawModal
          isOpen={isWithdrawModalOpen}
          setIsOpen={setIsWithdrawModalOpen}
          setIsConfirmModalOpen={setIsConfirmModalOpen}
          amount={amount}
          setAmount={setAmount}
          data={data}
          currency={currencyFromDecode}
          setCurrency={setCurrencyFromDecode}
          appDetails={appDetails}
          currencyExchangeRate={currencyExchangeRate}
          errorMsg={errorMsg}
          isShowErrorModal={isShowErrorModal}
          setIsShowErrorModal={setIsShowErrorModal}
        />
      )}
      {isConfirmModalOpen && (
        <WithdrawSuccessPage
          isOpen={isConfirmModalOpen}
          setIsOpen={setIsConfirmModalOpen}
          amount={amount}
          currency={currencyFromDecode}
          display_name={display_name}
          isLnUrlMethodReceive={
            data?.withdraw_method === lnurl && data?.tag === withdrawRequest
          }
        />
      )}
    </Dialog>
  );
}

export default InstalledAppDialog;
